// GroundTruth QA pivot panel — renders one of two pivots selected via
// the dropdown. Layout mirrors the wireframe (label · select · issue
// badge, then a table). Used only on Sidekick's GT summary pane.

import { useMemo } from 'react';
import { buildLocationFilterPivot, buildDcmPlacementPivot } from './gtPivots';

export function GtQaPivotPanel({ ctsReview, activeKey, setActiveKey }) {
  const locPivot = useMemo(() => buildLocationFilterPivot(ctsReview), [ctsReview]);
  const dcmPivot = useMemo(() => buildDcmPlacementPivot(ctsReview), [ctsReview]);
  if (!ctsReview?.result?.adgroups?.length) return null;
  const options = [
    { key: 'location', label: 'Location filters — ZIP code assignment', issues: locPivot.issueCount },
    { key: 'dcm', label: 'DCM placements — count by ad group', issues: dcmPivot.issueCount },
  ];
  const active = options.find((o) => o.key === activeKey) || options[0];
  return (
    <div className="clw-ps-table-wrap">
      <div className="clw-ps-table-toolbar">
        <span className="clw-ps-table-label">Breakdown</span>
        <select
          className="clw-ps-table-select"
          value={active.key}
          onChange={(e) => setActiveKey(e.target.value)}
        >
          {options.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
        <span className="clw-ps-table-badge">
          {active.issues > 0
            ? `${active.issues} issue${active.issues === 1 ? '' : 's'} found`
            : 'No issues'}
        </span>
      </div>
      {active.key === 'location'
        ? <GtLocationFilterTable pivot={locPivot} />
        : <GtDcmPlacementTable pivot={dcmPivot} />}
    </div>
  );
}

function GtLocationFilterTable({ pivot }) {
  return (
    <div className="clw-ps-tbl-scroll">
      <table className="clw-ps-tbl">
        <thead>
          <tr>
            <th>AD GROUP</th>
            <th>LOCATION FILTER</th>
            <th className="num">ZIP CODES</th>
            <th className="num">TOTAL FILTERS</th>
            <th>MISSING FILTER</th>
          </tr>
        </thead>
        <tbody>
          {pivot.rows.map((row) => (
            <tr key={row.rowId} className={row.status !== 'ok' ? 'flagged' : ''}>
              <td title={row.ad_group_name}>{row.ad_group_name}</td>
              <td>{row.location_filter}</td>
              <td className="num">{row.zipCount}</td>
              <td className="num">{row.totalCount}</td>
              <td>
                {row.status === 'ok'
                  ? <span className="clw-qa-cell-pill ok">OK</span>
                  : row.status === 'missing'
                    ? <span className="clw-qa-cell-pill warn">No filter set</span>
                    : <span className="clw-qa-cell-pill warn">No matching list</span>}
              </td>
            </tr>
          ))}
          {pivot.rows.length === 0 && (
            <tr><td colSpan={5} className="empty">No ad groups to report on.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function GtDcmPlacementTable({ pivot }) {
  const { rows, sizeKeys, anyPopulated } = pivot;
  return (
    <div className="clw-ps-tbl-scroll">
      <table className="clw-ps-tbl">
        <thead>
          <tr>
            <th>AD GROUP</th>
            {sizeKeys.map((sz) => <th key={sz} className="num">{sz}</th>)}
            <th className="num">TOTAL</th>
            <th>MISSING SIZES</th>
            <th>DUPLICATES</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const flagged = row.missingSizes.length > 0 || row.duplicates > 0;
            return (
              <tr key={row.rowId} className={flagged ? 'flagged' : ''}>
                <td title={row.ad_group_name}>{row.ad_group_name}</td>
                {sizeKeys.map((sz) => (
                  <td key={sz} className="num">{row.counts[sz] > 0 ? row.counts[sz] : <span className="dim">—</span>}</td>
                ))}
                <td className="num">{row.total > 0 ? row.total : <span className="dim">—</span>}</td>
                <td>
                  {row.missingSizes.length === 0
                    ? <span className="clw-qa-cell-pill dim">—</span>
                    : <span className="clw-qa-cell-pill warn">{row.missingSizes.join(', ')}</span>}
                </td>
                <td>
                  {row.duplicates === 0
                    ? <span className="clw-qa-cell-pill dim">—</span>
                    : <span className="clw-qa-cell-pill warn">{row.duplicates}</span>}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={sizeKeys.length + 4} className="empty">No ad groups to report on.</td></tr>
          )}
        </tbody>
      </table>
      {!anyPopulated && rows.length > 0 && (
        <div className="clw-qa-empty-hint">
          No <code>placement_id_*</code> fields populated on any ad group yet — fill in DCM placement IDs in the Excel to see counts here.
        </div>
      )}
    </div>
  );
}
